import { Link } from "react-router-dom";
import { Instagram, Mail, BookOpen, Globe } from "lucide-react";
import { NewsletterSignup } from "./NewsletterSignup";

// TikTok has no lucide icon in this version, use inline svg
const TikTokIcon = ({ size = 18 }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="currentColor"
        aria-hidden="true"
    >
        <path d="M16.5 3a5.7 5.7 0 0 0 4.5 4.5v3a8.7 8.7 0 0 1-4.5-1.4v6.6a6.6 6.6 0 1 1-6.6-6.6c.3 0 .6 0 .9.1v3.1a3.6 3.6 0 1 0 2.6 3.4V3h3.1z" />
    </svg>
);

const SOCIALS = [
    { href: "https://instagram.com/Book.Blend1", label: "Instagram", testid: "footer-instagram", Icon: Instagram },
    { href: "https://tiktok.com/@bookblend1", label: "TikTok", testid: "footer-tiktok", Icon: TikTokIcon },
    { href: "https://www.thebookblend.com", label: "Website", testid: "footer-website-icon", Icon: Globe },
    { href: "mailto:BookBlend1@outlook.com", label: "Email", testid: "footer-email-icon", Icon: Mail },
];

const EXPLORE_LINKS = [
    { href: "/#features", label: "Features", testid: "footer-link-features" },
    { to: "/shop", label: "The Bag Drop", testid: "footer-link-bags" },
    { to: "/preorder", label: "Pre-order a Bag", testid: "footer-link-preorder" },
    { to: "/credits", label: "Credits", testid: "footer-link-credits" },
    { to: "/find-a-machine", label: "Find a Machine", testid: "footer-link-find-machine" },
    { to: "/membership", label: "Membership", testid: "footer-link-membership" },
    { href: "/#about", label: "About", testid: "footer-link-about" },
    { href: "/#faq", label: "FAQ", testid: "footer-link-faq" },
];

const COMPANY_LINKS = [
    { to: "/support", label: "Support", testid: "footer-link-support" },
    { to: "/pickup-and-refunds", label: "Refunds & Billing", testid: "footer-link-refund" },
    { to: "/privacy-policy", label: "Privacy Policy", testid: "footer-link-privacy" },
    { to: "/terms", label: "Terms of Service", testid: "footer-link-terms" },
    { href: "https://www.thebookblend.com", label: "thebookblend.com", testid: "footer-link-website", external: true },
    { href: "mailto:BookBlend1@outlook.com", label: "BookBlend1@outlook.com", testid: "footer-link-email" },
];

const FooterLink = ({ link }) =>
    link.to ? (
        <Link to={link.to} data-testid={link.testid} className="hover:text-[#C4956A]">
            {link.label}
        </Link>
    ) : (
        <a
            href={link.href}
            data-testid={link.testid}
            className="hover:text-[#C4956A]"
            {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
            {link.label}
        </a>
    );

const LinkColumn = ({ title, links }) => (
    <div>
        <h4 className="font-serif text-xl mb-4 text-white">{title}</h4>
        <ul className="space-y-3 text-sm text-[#FDF6EE]/70">
            {links.map((link) => (
                <li key={link.testid}>
                    <FooterLink link={link} />
                </li>
            ))}
        </ul>
    </div>
);

const SocialIcons = () => (
    <div className="flex items-center gap-3 pt-2">
        {SOCIALS.map(({ href, label, testid, Icon }) => (
            <a
                key={testid}
                href={href}
                {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                aria-label={label}
                data-testid={testid}
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#C4956A] hover:border-[#C4956A] transition-colors"
            >
                <Icon size={18} />
            </a>
        ))}
    </div>
);

const BrandBlock = () => (
    <div className="md:col-span-2 space-y-5">
        <Link to="/" className="flex items-center gap-2.5 w-fit" data-testid="footer-logo">
            <span className="w-10 h-10 rounded-full bg-[#C4956A] flex items-center justify-center text-white">
                <BookOpen size={20} strokeWidth={2.2} />
            </span>
            <span className="font-serif text-2xl font-semibold">BookBlend</span>
        </Link>
        <p className="text-[#FDF6EE]/70 max-w-md leading-relaxed">
            A cozy home for book lovers. Grab a curated bag at a BookBlend
            vending machine, scan the code, and join a community that reads.
        </p>
        <div className="pt-2">
            <h4 className="font-serif text-lg mb-2 text-white">Machine news, monthly-ish</h4>
            <p className="text-sm text-[#FDF6EE]/60 mb-3">
                New machines, new bags — no spam, ever.
            </p>
            <NewsletterSignup />
        </div>
        <SocialIcons />
    </div>
);

const BottomBar = () => (
    <div className="border-t border-white/10 mt-14 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-[#FDF6EE]/60">
        <p data-testid="footer-copyright">© 2025 BookBlend. All rights reserved.</p>
        <div className="flex items-center gap-4">
            <p>Crafted with care in Milwaukee, Wisconsin.</p>
            <Link
                to="/admin"
                data-testid="footer-admin-link"
                className="text-[#FDF6EE]/40 hover:text-[#C4956A] transition-colors"
            >
                Admin
            </Link>
        </div>
    </div>
);

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="relative mt-24 bg-[#2D2D2D] text-[#FDF6EE]"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 py-16">
                <div className="grid md:grid-cols-4 gap-12">
                    <BrandBlock />
                    <LinkColumn title="Explore" links={EXPLORE_LINKS} />
                    <LinkColumn title="Company" links={COMPANY_LINKS} />
                </div>
                <BottomBar />
            </div>
        </footer>
    );
}
